import presizeBackend from '../../services/presize-backend';
import presizeBackendRest from '../../services/presize-backend-rest';
import { isDbMigrated } from '../../lib/rest-feature-flag';
import { SizingStore } from './sizing-store';
import { runInAction } from 'mobx';
import { SizingGroup } from '../../models/sizing-group';
import { getPreselectedSizeChartId } from '../../lib/article-utils';
import { ApplyReviewGroupInput } from '../../models/request';

export class MarkAllGroupsReviewedAction {
  private async setAllGroupsReviewedValue(this: SizingStore, value: boolean): Promise<void> {
    // REST path (isDbMigrated=true): single PATCH for ALL article IDs in the order, then one
    // POST /article-groups to regenerate groups (is_reviewed is part of the group key).
    if (isDbMigrated()) {
      return this.setAllGroupsReviewedValueRest(value);
    }

    const groupsReviewedNotEqualValue = this.sizingGroups.filter((group) => group.isReviewed !== value);
    if (groupsReviewedNotEqualValue.length) {
      const newReviews = groupsReviewedNotEqualValue.map((sg) => ({
        groupKey: sg.groupKey,
        reviewed: value,
      }));
      const groupSizingData: ApplyReviewGroupInput[] = [];
      groupsReviewedNotEqualValue.forEach((sizingGroup: SizingGroup) => {
        if (sizingGroup.selectedSizeKey && sizingGroup.selectedSizeChart) {
          const { commodityGroup, groupKey, selectedSizeKey, selectedSizeChart, sizingNote, sizeStructure, sizing } =
            sizingGroup;
          groupSizingData.push({
            sizingGroup: {
              groupKey,
              commodityGroupCode: commodityGroup.code,
              preselectedSizeChartId: getPreselectedSizeChartId(sizingGroup),
            },
            sizeChartCode: selectedSizeChart.sizeChartCode,
            lengthChartCode: selectedSizeChart.lengthChartCode,
            sizeKeyId: selectedSizeKey.sizeKeyId,
            sizeKey: selectedSizeKey.sizeKey,
            sizeList: selectedSizeKey.sizeList,
            lengthList: selectedSizeKey.lengthList,
            sizeLengthSeparator: selectedSizeKey.sizeLengthSeparator,
            manualSizeKey: selectedSizeKey.manualSizeKey,
            manualSizeList: selectedSizeKey.manualSizeKey ? selectedSizeKey.sizeList : [],
            manualLengthList: selectedSizeKey.manualSizeKey ? selectedSizeKey.lengthList : [],
            manualSizeLengthSeparator: selectedSizeKey.manualSizeKey ? selectedSizeKey.sizeLengthSeparator : undefined,
            distribution: selectedSizeKey.distribution,
            distributionType: selectedSizeKey.distributionType,
            recoStrategy: selectedSizeKey?.recommendationStrategy,
            recoLevel: selectedSizeKey?.recommendationLevel,
            resetManualDistribution: false,
            referenceBrandCode: sizing?.referenceBrandCode,
            sizingNote,
            sizeStructure,
          });
        }
      });

      await presizeBackend.applyReview(this.referenceId, newReviews, groupSizingData);

      runInAction(() => groupsReviewedNotEqualValue.forEach((group) => (group.isReviewed = value)));
    }
  }

  /**
   * Mark/unmark all groups via REST (isDbMigrated=true, PT-57317).
   *
   * Sends a SINGLE PATCH for ALL article IDs in the order (not one per group) to set
   * is_reviewed, then one POST /article-groups to regenerate groups (is_reviewed is part of
   * the group key). Sizing is unchanged, so groups are remapped without re-fetching sizing.
   *
   * No-ops when every group already matches the target value. If the PATCH throws, the error
   * propagates and the regroup (POST) is not called.
   */
  private async setAllGroupsReviewedValueRest(this: SizingStore, value: boolean): Promise<void> {
    const hasGroupsToUpdate = this.sizingGroups.some((group) => group.isReviewed !== value);
    if (!hasGroupsToUpdate) {
      return;
    }

    await presizeBackendRest.updateArticles(
      this.referenceId,
      {
        article_reference_ids: this.articles.map((article) => article.referenceId),
        is_reviewed: value,
      },
      undefined,
      'application/merge-patch+json'
    );

    // Optimistically reflect the new review state on every group (the source of truth) so the
    // regroup request produces the correct new group keys. regroupPreservingSizing() syncs the
    // per-article reviewed flags from group.isReviewed before POSTing.
    runInAction(() => this.sizingGroups.forEach((group) => (group.isReviewed = value)));

    await this.regroupPreservingSizing();
  }

  async markAllGroupsAsReviewed(this: SizingStore): Promise<void> {
    await this.setAllGroupsReviewedValue(true);
  }

  async markAllGroupsAsNotReviewed(this: SizingStore): Promise<void> {
    await this.setAllGroupsReviewedValue(false);
  }
}
